import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";

import { useSession } from "./lib/session";
import { EnrollPage } from "./pages/auth/EnrollPage";
import { SignInPage } from "./pages/auth/SignInPage";
import { Page, SkeletonLines } from "./ui";
import { AppShell } from "./ui/shell";

const AccountsPage = lazy(() => import("./pages/accounts/AccountsPage").then((module) => ({ default: module.AccountsPage })));
const AuditPage = lazy(() => import("./pages/audit/AuditPage").then((module) => ({ default: module.AuditPage })));
const ConvoysPage = lazy(() => import("./pages/convoys/ConvoysPage").then((module) => ({ default: module.ConvoysPage })));
const DrivesPage = lazy(() => import("./pages/drives/DrivesPage").then((module) => ({ default: module.DrivesPage })));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage").then((module) => ({ default: module.NotFoundPage })));
const OperatorsPage = lazy(() => import("./pages/operators/OperatorsPage").then((module) => ({ default: module.OperatorsPage })));
const OverviewPage = lazy(() => import("./pages/overview/OverviewPage").then((module) => ({ default: module.OverviewPage })));
const ProfilePage = lazy(() => import("./pages/profile/ProfilePage").then((module) => ({ default: module.ProfilePage })));
const SystemPage = lazy(() => import("./pages/system/SystemPage").then((module) => ({ default: module.SystemPage })));

function PageFallback() {
  return (
    <Page>
      <SkeletonLines lines={6} />
    </Page>
  );
}

function Authenticated({ children }: { children: ReactNode }) {
  const { session, signOut } = useSession();
  const location = useLocation();
  if (session.status === "unauthenticated") {
    return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />;
  }
  const operator = session.status === "authenticated" ? session.info.operator : null;
  return (
    <AppShell user={operator} loading={session.status === "loading"} onSignOut={() => void signOut()}>
      {session.status === "authenticated" ? <Suspense fallback={<PageFallback />}>{children}</Suspense> : null}
    </AppShell>
  );
}

function SignInRoute() {
  const { session } = useSession();
  const location = useLocation();
  if (session.status === "authenticated") {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from && from !== "/sign-in" ? from : "/"} replace />;
  }
  return <SignInPage />;
}

export function App() {
  return (
    <Routes>
      <Route path="/sign-in" element={<SignInRoute />} />
      <Route path="/enroll" element={<EnrollPage />} />
      <Route
        path="*"
        element={
          <Authenticated>
            <Routes>
              <Route path="/" element={<OverviewPage />} />
              <Route path="/system" element={<SystemPage />} />
              <Route path="/accounts" element={<AccountsPage />} />
              <Route path="/convoys" element={<ConvoysPage />} />
              <Route path="/drives" element={<DrivesPage />} />
              <Route path="/operators" element={<OperatorsPage />} />
              <Route path="/audit" element={<AuditPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Authenticated>
        }
      />
    </Routes>
  );
}
